/**
 * Admin Panel Component (User Management)
 *
 * Unified admin dashboard for managing users, subscriptions, and audit logs
 * Requires admin role to access
 *
 * Features:
 * - User list with search and filters
 * - Subscription management
 * - User CRUD operations (create, edit, delete)
 * - Audit log viewer
 *
 * T082: Create admin-panel.ts - Main admin dashboard with admin role check
 */

import { auth } from '@/core/firebase'
import { initializeUserList } from './user-list'
import { initializeSubscriptionEditor } from './subscription-editor'
import { initializeAuditLog } from './audit-log'
import { initializeUserCRUD, showCreateUserModal, loadAllUsersFromFirestore, showEditUserModal, deleteUser } from './user-crud-modals'

import { ref, uploadBytes } from 'firebase/storage'
import { storage } from '@/core/firebase'
import { currentUser } from '@/core/state'

import { collection, getDocs, doc, deleteDoc } from 'firebase/firestore'
import { db } from '@/core/firebase'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/core/firebase'

let isAdminPanelInitialized = false;
/**
 * Check if current user has admin role (from Firestore)
 */
async function isAdmin(): Promise<boolean> {
  const currentUser = auth.currentUser
  if (!currentUser) {
    console.error('[Admin Panel] No current user found')
    return false
  }

  try {
    // Check admin status from Firestore adminUsers collection
    const { isUserAdmin } = await import('@/store/api')
    const adminStatus = await isUserAdmin(currentUser.uid)
    console.log('[Admin Panel] Admin status from Firestore:', adminStatus)
    return adminStatus
  } catch (error) {
    console.error('[Admin Panel] Error checking admin role:', error)
    return false
  }
}

/**
 * Redirect to home page if not admin
 */
function redirectIfNotAdmin(): void {
  window.location.href = '/'
}

/**
 * [Job 30 - Part 3] Loads and renders the table of current demo files (Sales & P&L)
 */
async function loadDemoDataTable(): Promise<void> {
  const container = document.getElementById('demo-data-table-container');
  if (!container) return;

  container.innerHTML = `<div class="p-8 text-center text-gray-500">Loading demo file list...</div>`;

  try {
    // This path is from Job 25: demoTemplate/metadata/files
    const metadataRef = collection(db, 'demoTemplate/metadata/files');
    const metadataSnap = await getDocs(metadataRef);

    if (metadataSnap.empty) {
      container.innerHTML = `<div class="p-8 text-center text-gray-500">No demo data files have been uploaded.</div>`;
      return;
    }

    const files = metadataSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    container.innerHTML = `
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data Type</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch Name(s)</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period / Range</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody id="demo-data-table-body" class="bg-white divide-y divide-gray-200">
          ${files.map(file => {
            // --- START OF FIX: Smart display logic ---
            let dataType = 'Unknown';
            let period = file.periodRange || 'N/A';
            let dataTypeClass = 'bg-gray-100 text-gray-800';
            
            if (file.dataType === 'pnl') {
              dataType = 'P&L';
              period = file.periodRange; // For P&L, this is just "YYYY-MM"
              dataTypeClass = 'bg-blue-100 text-blue-800';
            } else if (file.dataType === 'sales') {
              dataType = 'Sales';
              // For Sales, periodRange is "YYYY-MM-DD to YYYY-MM-DD"
              dataTypeClass = 'bg-green-100 text-green-800';
            }
            // --- END OF FIX ---

            return `
              <tr id="demo-file-row-${file.id}">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">${file.fileName}</td>
                
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${dataTypeClass}">
                    ${dataType}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${file.branchName}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${period}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${file.uploadedAt ? new Date(file.uploadedAt.toDate()).toLocaleDateString() : 'N/A'}</td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button 
                    class="delete-demo-data-btn text-red-600 hover:text-red-900"
                    data-doc-id="${file.id}"
                    data-file-name="${file.fileName}"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            `
          }).join('')}
        </tbody>
      </table>
    `;
  } catch (error) {
    console.error('[loadDemoDataTable] Error:', error);
    container.innerHTML = `<div class="p-8 text-center text-red-500">Failed to load demo file list.</div>`;
  }
}

/**
 * Initialize the admin panel
 *
 * Checks admin role and initializes sub-components:
 * - User list table
 * - Subscription editor
 */
export async function initializeAdminPanel(): Promise<void> {
  console.log('[Admin Panel] Starting initialization...')

  // Check if user is admin
  const hasAdminRole = await isAdmin()

  if (!hasAdminRole) {
    const currentUser = auth.currentUser
    const errorMsg = currentUser
      ? `Access denied. Your account (${currentUser.email}) does not have admin role. Please contact an administrator to assign admin privileges.`
      : 'Access denied. You must be logged in with an admin account.'

    console.error('[Admin Panel] Admin check failed:', errorMsg)
    alert(errorMsg)
    redirectIfNotAdmin()
    return
  }

  console.log('[Admin Panel] Admin check passed, initializing with admin role')

  // Show admin panel
  const adminPanel = document.getElementById('admin-panel')
  if (adminPanel) {
    adminPanel.classList.remove('hidden')
  }

  // Hide other views
  const mainContent = document.getElementById('main-content')
  if (mainContent) {
    mainContent.classList.add('hidden')
  }

  // Initialize sub-components
  try {
    initializeUserList()
    initializeSubscriptionEditor()
    initializeAuditLog()
    initializeUserCRUD()

    // Setup tab switching
    setupTabSwitching()

    // Setup user CRUD buttons
    setupUserCRUDButtons()

    const adminPanelElement = document.getElementById('admin-panel');
    adminPanelElement?.addEventListener('click', async (e) => {
      const target = e.target as HTMLElement;

      // Check if a delete button on the demo table was clicked
      if (target.classList.contains('delete-demo-data-btn')) {
        const metadataDocId = target.getAttribute('data-doc-id');
        const fileName = target.getAttribute('data-file-name') || 'this file';

        if (!metadataDocId) {
          alert('Error: No document ID found.');
          return;
        }

        if (!confirm(`Are you sure you want to delete ${fileName}? This will remove all its associated demo data.`)) {
          return;
        }

        target.textContent = 'Deleting...';
        target.setAttribute('disabled', 'true');

        try {
          const deleteDemoDataFile = httpsCallable(functions, 'deleteDemoDataFile');
          await deleteDemoDataFile({ metadataDocId });
          
          // Remove the row from the table on success
          document.getElementById(`demo-file-row-${metadataDocId}`)?.remove();
          alert('Demo data file deleted successfully.');

        } catch (error: any) {
          console.error('[DELETE_DEMO] Error:', error);
          alert(`Failed to delete file: ${error.message}`);
          target.textContent = 'Delete';
          target.removeAttribute('disabled');
        }
      }
    });

    const uploadDemoBtn = document.getElementById('upload-demo-data-btn')
      uploadDemoBtn?.addEventListener('click', async () => {
        const fileInput = document.getElementById('demo-data-file-input') as HTMLInputElement
        const feedbackEl = document.getElementById('demo-upload-feedback')
        const file = fileInput.files?.[0]
    
        if (!file || !currentUser || !feedbackEl) {
          alert('Please select a file first.')
          return
        }
    
        (uploadDemoBtn as HTMLButtonElement).disabled = true
        feedbackEl.className = 'p-3 text-sm rounded-md bg-blue-100 text-blue-800'
        feedbackEl.textContent = 'Uploading file... Please wait. This may take a moment.'
        feedbackEl.classList.remove('hidden')
    
        try {
          const storagePath = `demo_uploads/${currentUser.uid}/${file.name}`
          const storageRef = ref(storage, storagePath)
    
          // Upload the file to the secure path. The backend function will trigger automatically.
          await uploadBytes(storageRef, file)
    
          feedbackEl.className = 'p-3 text-sm rounded-md bg-green-100 text-green-800'
          feedbackEl.textContent = 'Upload complete! The backend is now processing the file to update the demo data. This can take up to a minute.'
        } catch (error: any) {
          console.error('Demo data upload failed:', error)
          feedbackEl.className = 'p-3 text-sm rounded-md bg-red-100 text-red-800'
          feedbackEl.textContent = `Upload failed: ${error.message}`
        } finally {
          (uploadDemoBtn as HTMLButtonElement).disabled = false
          fileInput.value = '' // Reset the file input
        }
      })

    console.log('[Admin Panel] Initialized successfully')
  } catch (error) {
    console.error('[Admin Panel] Initialization error:', error)
    alert('Error initializing admin panel. Please check console for details.')
  }
}

/**
 * Setup tab switching functionality
 */
function setupTabSwitching(): void {
  const tabs = document.querySelectorAll('.admin-tab')
  const tabContents = document.querySelectorAll('.admin-tab-content')

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const targetTab = tab.getAttribute('data-tab')

      // Update active tab
      tabs.forEach((t) => t.classList.remove('border-indigo-500', 'text-indigo-600'))
      tabs.forEach((t) => t.classList.add('border-transparent', 'text-gray-500'))
      tab.classList.remove('border-transparent', 'text-gray-500')
      tab.classList.add('border-indigo-500', 'text-indigo-600')

      // Update visible content
      tabContents.forEach((content) => {
        const contentTab = content.getAttribute('data-tab-content')
        if (contentTab === targetTab) {
            content.classList.remove('hidden')
            
            // --- START OF FIX: Load table when tab is clicked ---
            if (targetTab === 'demo-data') {
              loadDemoDataTable();
            }
            // --- END OF FIX ---
            
          } else {
            content.classList.add('hidden')
          }
      })
    })
  })
}

/**
 * Setup user CRUD buttons
 */
function setupUserCRUDButtons(): void {
  // Create user button
  const createBtn = document.getElementById('create-user-btn')
  createBtn?.addEventListener('click', () => {
    showCreateUserModal()
  })

  // Load users button (for CRUD tab)
  const loadBtn = document.getElementById('load-crud-users-btn')
  loadBtn?.addEventListener('click', async () => {
    await renderCRUDUsersTable()
  })

  // Initial load of CRUD users table
  renderCRUDUsersTable()
}

/**
 * Render users table for CRUD operations tab
 */
async function renderCRUDUsersTable(): Promise<void> {
  const container = document.getElementById('crud-users-container')
  if (!container) return

  container.innerHTML = `
    <div class="flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      <span class="ml-3 text-gray-600">Loading users...</span>
    </div>
  `

  try {
    const users = await loadAllUsersFromFirestore()

    if (users.length === 0) {
      container.innerHTML = `
        <div class="text-center py-8 text-gray-500">
          No users found in Firestore.
        </div>
      `
      return
    }

    const tableHTML = `
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User ID
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            ${users.map((user) => `
              <tr class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ${user.email || 'N/A'}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="px-2 py-1 text-xs font-semibold rounded-full ${user.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}">
                    ${user.role}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                  ${user.uid}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    class="crud-edit-btn text-indigo-600 hover:text-indigo-900"
                    data-uid="${user.uid}"
                  >
                    Edit
                  </button>
                  <button
                    class="crud-delete-btn text-red-600 hover:text-red-900"
                    data-uid="${user.uid}"
                    data-email="${user.email || ''}"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `

    container.innerHTML = tableHTML

    // Add event listeners for edit/delete buttons
    container.querySelectorAll('.crud-edit-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const uid = btn.getAttribute('data-uid')
        const user = users.find((u) => u.uid === uid)
        if (user) {
          showEditUserModal(user)
        }
      })
    })

    container.querySelectorAll('.crud-delete-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const uid = btn.getAttribute('data-uid')
        const email = btn.getAttribute('data-email')
        if (uid) {
          deleteUser(uid, email)
        }
      })
    })
  } catch (error: unknown) {
    console.error('[Admin Panel] Error loading CRUD users:', error)
    container.innerHTML = `
      <div class="text-center py-8">
        <div class="text-red-600 font-medium mb-2">Error Loading Users</div>
        <div class="text-gray-600 text-sm">${(error as Error).message || 'Unknown error occurred'}</div>
      </div>
    `
  }
}

/**
 * Render admin panel HTML structure
 *
 * Creates the admin panel layout with:
 * - Header with title
 * - User list table
 * - Subscription editor modal
 *
 * Should be called during app initialization to inject HTML
 */
export function renderAdminPanelHTML(): void {
  const container = document.getElementById('app')
  if (!container) {
    console.error('[Admin Panel] App container not found')
    return
  }

  const adminPanelHTML = `
    <div id="admin-panel" class="premium-analysis-content hidden bg-gray-50 py-8 w-full">
      <div class="px-4 sm:px-6 lg:px-8">
        <div class="mb-8">
          <h1 class="text-3xl font-bold text-gray-900">User Management</h1>
          <p class="mt-2 text-sm text-gray-600">
            Manage users, subscriptions, and audit logs
          </p>
        </div>

        <div class="bg-white rounded-lg shadow-sm">
          <div class="border-b border-gray-200">
            <nav class="flex -mb-px">
              <button
                class="admin-tab px-6 py-4 text-sm font-medium border-b-2 border-indigo-500 text-indigo-600 hover:text-indigo-700 transition-colors"
                data-tab="user-list"
              >
                User List & Subscriptions
              </button>
              <button
                class="admin-tab px-6 py-4 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 transition-colors"
                data-tab="user-crud"
              >
                User CRUD
              </button>
              <button
                class="admin-tab px-6 py-4 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 transition-colors"
                data-tab="audit-log"
              >
                Audit Log
              </button>
              <button
                class="admin-tab px-6 py-4 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 transition-colors"
                data-tab="demo-data"
              >
                Demo Data
              </button>
              </nav>
          </div>

          <div class="admin-tab-content p-6" data-tab-content="user-list">
            <div class="mb-6">
              <h2 class="text-xl font-semibold text-gray-900 mb-4">User List & Subscriptions</h2>

              <div class="flex gap-4 mb-4">
                <div class="flex-1">
                  <label for="search-email" class="block text-sm font-medium text-gray-700 mb-1">
                    Search by Email
                  </label>
                  <input
                    type="email"
                    id="search-email"
                    placeholder="user@example.com"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div class="w-48">
                  <label for="filter-plan-type" class="block text-sm font-medium text-gray-700 mb-1">
                    Filter by Plan
                  </label>
                  <select
                    id="filter-plan-type"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">All Plans</option>
                    <option value="free">Free</option>
                    <option value="standard">Standard</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>

                <div class="flex items-end">
                  <button
                    id="search-users-btn"
                    class="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>

            <div id="user-list-container"></div>

            <div id="pagination-container" class="mt-4 flex justify-between items-center"></div>
          </div>

          <div class="admin-tab-content p-6 hidden" data-tab-content="user-crud">
            <div class="mb-6">
              <div class="flex items-center justify-between mb-4">
                <h2 class="text-xl font-semibold text-gray-900">User CRUD Operations</h2>
                <div class="flex gap-2">
                  <button
                    id="create-user-btn"
                    class="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
                  >
                    + Create User
                  </button>
                  <button
                    id="load-crud-users-btn"
                    class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div class="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p class="text-sm text-blue-800">
                  <strong>Note:</strong> This tab provides direct Firestore CRUD operations.
                  User creation via frontend has limitations (requires admin re-authentication).
                  For subscription management, use the "User List & Subscriptions" tab.
                </p>
              </div>
            </div>

            <div id="crud-users-container"></div>
          </div>

          <div class="admin-tab-content p-6 hidden" data-tab-content="audit-log">
            <div class="mb-6">
              <h2 class="text-xl font-semibold text-gray-900 mb-4">Admin Audit Log</h2>

              <div class="flex gap-4 mb-4">
                <div class="flex-1">
                  <label for="filter-action" class="block text-sm font-medium text-gray-700 mb-1">
                    Filter by Action
                  </label>
                  <select
                    id="filter-action"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">All Actions</option>
                    <option value="ASSIGN_ADMIN_ROLE">Assign Admin Role</option>
                    <option value="REVOKE_ADMIN_ROLE">Revoke Admin Role</option>
                    <option value="LIST_USERS">List Users</option>
                    <option value="UPDATE_USER_SUBSCRIPTION">Update Subscription</option>
                    <option value="DELETE_USER">Delete User</option>
                    <option value="UPDATE_PLAN_CONFIG">Update Plan Config</option>
                    <option value="FORCE_UPGRADE_TIER">Force Upgrade Tier</option>
                    <option value="MANUAL_SUBSCRIPTION_EDIT">Manual Subscription Edit</option>
                  </select>
                </div>

                <div class="flex-1">
                  <label for="filter-performed-by" class="block text-sm font-medium text-gray-700 mb-1">
                    Performed By (UID)
                  </label>
                  <input
                    type="text"
                    id="filter-performed-by"
                    placeholder="Admin UID"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div class="flex-1">
                  <label for="filter-target-user" class="block text-sm font-medium text-gray-700 mb-1">
                    Target User (UID)
                  </label>
                  <input
                    type="text"
                    id="filter-target-user"
                    placeholder="User UID"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div class="flex items-end gap-2">
                  <button
                    id="filter-audit-btn"
                    class="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
                  >
                    Filter
                  </button>
                  <button
                    id="clear-audit-filters-btn"
                    class="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>

            <div id="audit-log-container"></div>

            <div id="audit-pagination-container" class="mt-4 flex justify-between items-center"></div>
          </div>
          
         <div class="admin-tab-content p-6 hidden" data-tab-content="demo-data">
            <div id="demo-data-management-section" class="bg-white p-6 rounded-lg shadow-md border border-gray-200 mb-6">
                <h3 class="text-xl font-semibold text-gray-700 mb-1">Demo Data Management</h3>
                <p class="text-sm text-gray-500 mb-4">Upload a sales data file. Re-uploading a file with the same name will replace the existing data for that file.</p>
                
                <div id="demo-upload-feedback" class="hidden p-3 text-sm rounded-md mb-4"></div>
        
                <div class="flex items-center gap-4">
                    <input 
                        type="file" 
                        id="demo-data-file-input" 
                        accept=".xlsx, .xls, .csv" 
                        class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100" 
                    />
                    <button id="upload-demo-data-btn" class="bg-indigo-600 text-white font-semibold py-2 px-5 rounded-lg hover:bg-indigo-700 transition duration-300 whitespace-nowrap">
                        Upload & Replace Demo Data
                    </button>
                </div>
            </div>

            <div class="mt-8">
              <h3 class="text-xl font-semibold text-gray-900 mb-4">Current Demo Files</h3>
              <div id="demo-data-table-container" class="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                <div class="p-8 text-center text-gray-500">Loading demo file list...</div>
              </div>
            </div>
            </div>
          </div>
      </div>

      <div id="subscription-editor-modal"></div>
    </div>
  `

  // Insert admin panel HTML after main content
  const mainContent = document.getElementById('premium-manage-data-content')
  if (mainContent) {
    mainContent.insertAdjacentHTML('afterend', adminPanelHTML)
  } else {
    container.insertAdjacentHTML('beforeend', adminPanelHTML)
  }
}

/**
 * Navigate to admin panel (for use in navigation)
 */
export function navigateToAdminPanel(): void {
  // --- START OF FIX for JOB 36 ---
  // This function is called every time the tab is clicked,
  // but we only want to attach all the listeners *once*.
  if (isAdminPanelInitialized) {
    // Admin panel is already initialized, just show it.
    // The 'showContent' call below will handle this.
  } else {
    // This is the first time, run the full setup.
    initializeAdminPanel();
    isAdminPanelInitialized = true;
  }
  // --- END OF FIX ---

  const allMainContent = document.querySelectorAll('.premium-analysis-content')

  const showContent = (contentElement: HTMLElement | null, buttonElement: HTMLElement | null) => {
    allMainContent.forEach((el) => el.classList.add('hidden'))
    contentElement?.classList.remove('hidden')
    document.querySelectorAll('aside nav a').forEach((el) => el.classList.remove('bg-gray-100', 'font-semibold'))
    buttonElement?.classList.add('bg-gray-100', 'font-semibold')
  }

  // This part just shows the panel, which is fine to run every time.
  showContent(
    document.getElementById('admin-panel'),
    document.getElementById('premium-goto-user-management-btn'),
  )
}
